import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { EASE } from "@/components/Reveal";

const LINKS = [
  { to: "/hire", label: "Hire on demand", testid: "nav-hire-link" },
  { to: "/products/3d", label: "Excavator 3D", testid: "nav-3d-link" },
  { to: "/products/2.5d", label: "Excavator 2.5D", testid: "nav-25d-link" },
  { to: "/products/dozer", label: "Dozer 3D", testid: "nav-dozer-link" },
  { to: "/products/rover", label: "Rover", testid: "nav-rover-link" },
  { to: "/precision-solutions", label: "Why CHCNAV", testid: "nav-precision-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header data-testid="site-header" className="fixed inset-x-0 top-0 z-50 border-b border-signal/20 bg-ink/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center">
          <img src="/logo-lockup.png" alt="Green Grade" className="h-11 w-auto" />
        </Link>

        <nav className="hidden items-center gap-6 xl:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.25em] transition-colors ${
                  isActive ? "text-neon" : "text-fog hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/#quote"
            data-testid="nav-quote-cta"
            className="group hidden items-center gap-2 bg-brand px-5 py-3 font-display text-sm font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep sm:inline-flex"
          >
            Build your quote
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="flex h-10 w-10 items-center justify-center border border-line text-white lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-t border-line bg-ink/95 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-4 py-6 sm:px-8">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  className={({ isActive }) =>
                    `py-3 font-display text-3xl font-bold uppercase tracking-tight ${
                      isActive ? "text-neon" : "text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/#quote"
                data-testid="nav-mobile-quote-cta"
                className="mt-4 inline-flex w-fit items-center gap-2 bg-brand px-6 py-3 font-display text-base font-bold uppercase text-cream transition-colors hover:bg-signal hover:text-deep"
              >
                Build your quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
